const EventEmitter = require('events').EventEmitter
const { qs, build, apc } = require('../../libs/qs')

let _count = 0
let _msecsFirst = 0
let _msecsPrevious = 0

// Look-ahead scheduler constants
const LOOKAHEAD = 0.1          // seconds to schedule ahead of current time
const SCHEDULER_INTERVAL = 25  // ms between scheduler runs

const transport = {
    clock: null,
    playing: false,
    bpm: 120,
    _audioContext: null,
    _timerID: null,
    _nextTickTime: 0,
    _nextPosition: 0,
    _tickInterval: 0,

    init: (audioContext, options = {}) => {
        transport._audioContext = audioContext
        transport.clock = new EventEmitter()
        transport.setTempo(options.bpm || 120)

        if (options.container) {
            transport._bindExisting(options.container)
        }

        return transport
    },

    _scheduler: () => {
        while (transport._nextTickTime < transport._audioContext.currentTime + LOOKAHEAD) {
            transport.clock.emit('position', transport._nextPosition, transport._nextTickTime)
            transport._nextPosition++
            transport._nextTickTime += transport._tickInterval
        }
        transport._timerID = setTimeout(transport._scheduler, SCHEDULER_INTERVAL)
    },

    setTempo: (bpm) => {
        const val = Math.max(10, Math.min(999, Math.round(bpm)))
        transport.bpm = val
        transport._tickInterval = 60.0 / (val * 24)
        try { window.bpm = val } catch (e) { /* LFO setter may reference null instance */ }
        document.documentElement.style.setProperty('--crtseed', `${60 / val}s`)
        document.body.dispatchEvent(new CustomEvent('bpmchange', { detail: { bpm: val } }))
    },

    tap: () => {
        const now = Date.now()
        if (now - _msecsPrevious > 3000) _count = 0
        if (_count === 0) {
            _msecsFirst = now
            _count = 1
        } else {
            const avg = 60000 * _count / (now - _msecsFirst)
            transport.setTempo(Math.round(avg))
            _count++
        }
        _msecsPrevious = now
    },

    play: () => transport.start(),
    stop: () => transport.stop(),

    toggle: () => {
        if (transport.playing) {
            transport.stop()
        } else {
            transport.start()
        }
    },

    _bindExisting: (container) => {
        const tempoEl = container.querySelector('#tempo') || qs('#tempo')
        const tapBtn = container.querySelector('#taptempo') || qs('#taptempo')
        const upBtn = container.querySelector('#tempoup') || qs('#tempoup')
        const downBtn = container.querySelector('#tempodown') || qs('#tempodown')
        const playBtn = container.querySelector('.transport-playpause') || container.querySelector('#playpause') || qs('.transport-playpause') || qs('#playpause')

        if (tempoEl) {
            tempoEl.addEventListener('input', (e) => {
                transport.setTempo(Number(e.target.value))
            })
            document.body.addEventListener('bpmchange', (e) => {
                tempoEl.value = e.detail.bpm
            })
        }

        if (tapBtn) {
            tapBtn.addEventListener('click', () => transport.tap())
        }

        if (upBtn) {
            upBtn.addEventListener('click', () => {
                transport.setTempo(transport.bpm + 1)
                if (tempoEl) tempoEl.value = transport.bpm
            })
        }

        if (downBtn) {
            downBtn.addEventListener('click', () => {
                transport.setTempo(transport.bpm - 1)
                if (tempoEl) tempoEl.value = transport.bpm
            })
        }

        if (playBtn) {
            const syncPlayBtn = () => {
                playBtn.classList.toggle('active', transport.playing)
            }
            playBtn.addEventListener('click', () => {
                transport.toggle()
                syncPlayBtn()
            })
            document.body.addEventListener('startseq', syncPlayBtn)
            document.body.addEventListener('stopseq', syncPlayBtn)
        }
    },

    start: () => {
        transport.playing = true
        transport._nextTickTime = transport._audioContext.currentTime
        transport._nextPosition = 0
        if (transport._timerID !== null) {
            clearTimeout(transport._timerID)
            transport._timerID = null
        }
        transport._timerID = setTimeout(transport._scheduler, 0)
        document.body.dispatchEvent(new CustomEvent('startseq', { detail: { state: true } }))
        return transport
    },

    stop: () => {
        transport.playing = false
        if (transport._timerID !== null) {
            clearTimeout(transport._timerID)
            transport._timerID = null
        }
        document.body.dispatchEvent(new CustomEvent('stopseq', { detail: { state: false } }))
        return transport
    },

    on: (event, cb) => {
        if (transport.clock) transport.clock.on(event, cb)
        return transport
    },

    off: (event, cb) => {
        if (transport.clock) transport.clock.off(event, cb)
        return transport
    },

    injectUI: (mountEl, options = {}) => {
        const bpm = options.bpm || transport.bpm
        const box = build('div')
        box.classList.add('transportbox')
        box.innerHTML = `
            <div class="inptborder">
                <label>BPM</label>
                <input type="number" class="transport-tempo" min="10" max="999" step="1" value="${bpm}">
                <div id="tempoctrl">
                    <div class="transport-up">+</div>
                    <div class="transport-down">-</div>
                </div>
                <button class="transport-tap btnctrl">tap</button>
            </div>
            <div class="btnborder">
                <button class="transport-playpause btnctrl icons2"></button>
            </div>
        `

        const tempoEl = box.querySelector('.transport-tempo')
        const tapBtn = box.querySelector('.transport-tap')
        const upBtn = box.querySelector('.transport-up')
        const downBtn = box.querySelector('.transport-down')
        const playBtn = box.querySelector('.transport-playpause')

        tempoEl.addEventListener('input', (e) => {
            transport.setTempo(Number(e.target.value))
        })

        tapBtn.addEventListener('click', () => transport.tap())

        upBtn.addEventListener('click', () => {
            transport.setTempo(transport.bpm + 1)
            tempoEl.value = transport.bpm
        })

        downBtn.addEventListener('click', () => {
            transport.setTempo(transport.bpm - 1)
            tempoEl.value = transport.bpm
        })

        playBtn.addEventListener('click', () => {
            transport.toggle()
            playBtn.innerHTML = transport.playing ? '' : ''
            playBtn.classList.toggle('active', transport.playing)
        })

        document.body.addEventListener('bpmchange', (e) => {
            tempoEl.value = e.detail.bpm
        })

        apc(mountEl, box)
        return box
    }
}

module.exports = transport
